import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BugReportService } from '../../services/bug-report.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AlertService, FileUploadService } from 'src/app/shared-module/services';
import { BugReport, BugReportAttachment } from '../../models/bug-report';
import { ToastrService } from 'ngx-toastr';
import firebase from 'firebase/compat/app';
import 'firebase/compat/storage';
import { FeatureFlag, FeatureFlagsService } from 'src/app/clearpath-module/services/feature-flags.service';
import { environment } from 'src/environments/environment';

type Reference = firebase.storage.Reference;

@Component({
  selector: 'app-bug-reports',
  templateUrl: './bug-reports.component.html',
  styleUrls: ['./bug-reports.component.scss']
})
export class BugReportsComponent implements OnInit {

  bugReports: Array<BugReport> = []
  filteredBugReports: Array<BugReport> = []
  loading = false
  searchInput = ""
  searchForm: UntypedFormGroup = this.formBuilder.group({
    search: ["", Validators.required]
  });
  showResolved = false
  displayedColumns: string[] = ['preview', 'name', 'size'];
  migrateBugReportDealsEnabled = false;
  migrateBugReportDealsDebugEnv = '';

  constructor(
    private bugReportService: BugReportService,
    private formBuilder: UntypedFormBuilder,
    private alertService: AlertService,
    private toastrService: ToastrService,
    private route: ActivatedRoute,
    private fileUploadService: FileUploadService,
    private featureFlagsService: FeatureFlagsService,
  ) { }

  ngOnInit(): void {
    this.migrateBugReportDealsEnabled = this.featureFlagsService.getFeatureFlag(FeatureFlag.MigrateBugReportDeals);
    this.featureFlagsService.onFlagUpdated(FeatureFlag.MigrateBugReportDeals, (value) => {
      this.migrateBugReportDealsEnabled = value;
    });
    this.migrateBugReportDealsDebugEnv = environment.debugEnvironment;

    this.getAllBugReports()
  }

  getAllBugReports() {
    this.loading = true
    this.bugReportService.getAll().subscribe({
      next: bugReports => {
        this.bugReports = bugReports || []

        for (let i = 0; i < this.bugReports?.length; i++) {
          this.fileUploadService.getAttachmentsForBugReport(this.bugReports[i].id).subscribe(attachmentRefs => {
            Promise.all(
              attachmentRefs.items.map(item => this.getDataForAttachment(item))
            ).then(attachments => {
              this.bugReports[i].attachments = attachments
            })
          })
        }

        const searchParam = this.route.snapshot.queryParamMap.get('search');
        if (searchParam) {
          let matchingBugReports = this.bugReports.filter((report) => report.id === searchParam)
          if (matchingBugReports.length) {
            this.showResolved = matchingBugReports[0].resolved
          }
          this.searchForm.patchValue({ search: searchParam });
          this.searchInput = searchParam.trim();
        }

        this.loading = false
        this.filter();
      },
      error: error => {
        this.alertService.error(error);
        this.loading = false
      }
    })
  }

  getDataForAttachment(ref: Reference): Promise<BugReportAttachment> {
    return new Promise(resolve => {
      Promise.all([
        ref.getMetadata(),
        ref.getDownloadURL()
      ]).then(([
        metadata,
        downloadUrl
       ]) => {
        const sizeStr = this.formatBytes(Number(metadata.size ?? 0));
        resolve({
          name: metadata.name,
          size: sizeStr,
          downloadUrl,
          contentType: metadata.contentType
        })
      })
    })
  }

  formatBytes(bytes: number): string {
    if (!bytes || bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const value = bytes / Math.pow(1024, i);
    const precision = i === 0 ? 0 : 2;
    return `${parseFloat(value.toFixed(precision))} ${units[i]}`;
  }

  onSearch(formValues) {
    const sanitizedInput = formValues.search.trim().toLowerCase();
    this.searchInput = sanitizedInput;
    this.filter()
  }

  filter() {
    this.filteredBugReports = []
    this.bugReports.forEach(bugReport => {
      let customerName = (bugReport.deal.customer.firstName + " " + bugReport.deal.customer.lastName).toLowerCase()

      if (
        (
          bugReport.id === this.searchInput ||
          bugReport.form.description.toLowerCase().includes(this.searchInput) ||
          bugReport.form.reporterName.toLowerCase().includes(this.searchInput) ||
          bugReport.form.reporterEmail.toLowerCase().includes(this.searchInput) ||
          customerName.includes(this.searchInput)
        ) && bugReport.resolved == this.showResolved
      ) {
        this.filteredBugReports.push(bugReport)
      }
    })

    this.filteredBugReports.sort((a, b) => {
      let dateA = new Date(a.form.reportDate);
      let dateB = new Date(b.form.reportDate);
      return dateB.getTime() - dateA.getTime();
    })
  }

  searchActive(): boolean {
    return this.searchInput !== "";
  }

  onClearSearch() {
    this.searchForm.patchValue({search: ""});
    this.searchInput = "";
    this.filter();
  }

  downloadJson(bugReport: BugReport, file: string) {
    let data: any;
    const dateStr = new Date(bugReport.form.reportDate ?? Date.now()).toISOString().split('T')[0];

    if (file === "deal") {
      data = bugReport.deal;
    } else if (file === "vehicle") {
      data = bugReport.vehicle;
    } else {
      return;
    }

    const filename = `${file}-${dateStr}.json`;

    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();

    URL.revokeObjectURL(url);
  }

  resolve(id: string) {
    this.bugReportService.resolveBugReport(id).subscribe(() => {
      this.toastrService.info("Bug report marked as resolved")
    })

    this.bugReports.forEach(bugReport => {
      if (bugReport.id == id) {
        bugReport.resolved = true
          this.filter()
      }
    })
  }

  unresolve(id: string) {
    this.bugReportService.unresolveBugReport(id).subscribe(() => {
      this.toastrService.info("Bug report marked as unresolved")
    })

    this.bugReports.forEach(bugReport => {
      if (bugReport.id == id) {
        bugReport.resolved = false
        this.filter()
      }
    })
  }

  toggleResolvedView() {
    this.showResolved = !this.showResolved
    this.filter()
  }

  isImageAttachment(attachment: BugReportAttachment): boolean {
    return !!attachment?.contentType?.startsWith('image/');
  }

  attachmentIcon(attachment: BugReportAttachment): string {
    if (attachment?.contentType === 'application/pdf') return 'picture_as_pdf';
    if (attachment?.contentType?.startsWith('video/')) return 'movie';
    return 'insert_drive_file';
  }

  downloadAttachment(attachment: BugReportAttachment) {
    if (!attachment || !attachment.downloadUrl) {
      this.alertService.error('No attachment URL available');
      return;
    }

    try {
      const newWindow = window.open(attachment.downloadUrl, '_blank');
      if (!newWindow) {
        this.alertService.error('Popup blocked. Please allow popups to view the attachment.');
      }
    } catch (err) {
      console.error('Failed to open attachment', err);
      this.alertService.error('Failed to open attachment');
    }
  }

  useDebugEnv(url: string): string {
    if (!url) {
      return '';
    }

    try {
      const parsedUrl = new URL(url);
      const debugOrigin = new URL(this.migrateBugReportDealsDebugEnv).origin;
      return `${debugOrigin}${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    } catch {
      return url;
    }
  }
}
